import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useRef} from 'react';
import styled from 'styled-components';

// The composer's two modes live here rather than in the footer that renders the switch: this is
// the component that has to know which of them is chosen, and one vocabulary beats two that have
// to agree.
export const COMPOSER_MODE = {
	COMMENT: 'comment',
	REPLY_TO_NOVA: 'reply',
} as const;

export type ComposerMode = (typeof COMPOSER_MODE)[keyof typeof COMPOSER_MODE];

export interface NovaComposerModeSwitchProps {
	value: ComposerMode;
	onValueChange: (value: ComposerMode) => void;
	// Names the group as a whole for a screen reader; each chip carries its own visible text as its
	// own name.
	label: string;
	commentLabel: string;
	replyLabel: string;
	// Whether replying is available to this person at all. A person who may not act on the task may
	// not start a run, and a reply starts one — so the chip stays where it is and says why, rather
	// than disappearing and leaving the composer looking like it never had two modes.
	replyDisabled?: boolean;
	// Why replying is unavailable. Rendered as the chip's own title and read to assistive tech, the
	// same way the row control states its disabled reason.
	replyDisabledReason?: string;
}

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

const SELECTED_SURFACE = sys?.background.primary['1'] ?? '';
const SELECTED_BORDER = ref?.blue['100'] ?? '';
const SELECTED_TEXT = sys?.text.content.primary ?? '';
const RESTING_SURFACE = sys?.background.neutral['1'] ?? '';
const RESTING_BORDER = sys?.border.decorative ?? '';
const RESTING_TEXT = sys?.text.content.auxiliary ?? '';
const MUTED_ICON = sys?.iconfill.ondisabled ?? '';
const CHIP_RADIUS = size?.borderradius['3'] ?? '';
const FOCUS_RING = sys?.border.focusring ?? '';

const Group = styled.div`
	display: flex;
	gap: 6px;
`;

// Visually hidden, but kept in the accessibility tree — the title above covers a sighted user on
// hover, and this is what aria-describedby reads. Clipped rather than display:none, which would
// take it out of the tree altogether.
const HiddenReason = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

let replyReasonIdCounter = 0;

// Two separate chips rather than one joined track: only the chosen mode is tinted, and the
// untinted one reads as the alternative on offer instead of as the inactive half of a control.
// Built from tokens because the design system's own mode control is a single segmented track,
// which is a different shape - see this component's own note in the composer's spec.
const Chip = styled.button<{isSelected: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 6px 12px;
	border-radius: ${CHIP_RADIUS};
	border: 1px solid ${props => (props.isSelected ? SELECTED_BORDER : RESTING_BORDER)};
	background: ${props => (props.isSelected ? SELECTED_SURFACE : RESTING_SURFACE)};
	color: ${props => (props.isSelected ? SELECTED_TEXT : RESTING_TEXT)};
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	cursor: pointer;

	svg path {
		fill: ${props => (props.isSelected ? SELECTED_TEXT : MUTED_ICON)};
	}

	&:focus-visible {
		outline: 2px solid ${FOCUS_RING};
		outline-offset: 1px;
	}

	&:disabled {
		color: ${MUTED_ICON};
		cursor: not-allowed;
	}

	&:disabled svg path {
		fill: ${MUTED_ICON};
	}
`;

// A radiogroup, not two independent toggles: the two modes are one choice with one answer, and
// that is what tells a screen reader "1 of 2" rather than announcing two unrelated pressed states.
// Roving tabindex and arrow keys are what the role then obliges - a radiogroup that only responds
// to Tab and Space is the role without the behaviour it promises.
export const NovaComposerModeSwitch = (props: NovaComposerModeSwitchProps): JSX.Element => {
	const commentRef = useRef<HTMLButtonElement | null>(null);
	const replyRef = useRef<HTMLButtonElement | null>(null);
	// Per instance, so two switches on one page never describe each other's chip.
	const reasonIdRef = useRef<string>();
	if (!reasonIdRef.current) {
		replyReasonIdCounter += 1;
		reasonIdRef.current = `nova-reply-mode-reason-${replyReasonIdCounter}`;
	}
	const replyReasonId = reasonIdRef.current;
	const showReason = !!props.replyDisabled && !!props.replyDisabledReason;

	const select = (mode: ComposerMode): void => {
		if (mode === props.value) return;
		// The one place a mode change can be refused. Guarding here rather than only on the chip's
		// own click covers the keyboard route too — arrow keys move selection, so a disabled chip
		// that only refused clicks would still be reachable by pressing right.
		if (mode === COMPOSER_MODE.REPLY_TO_NOVA && props.replyDisabled) return;
		props.onValueChange(mode);
	};

	// Either arrow direction moves to the other chip, since there are only two: with one pair there
	// is no difference between "next" and "previous", and refusing one direction would just look
	// broken. Selection follows focus, which is the expected radiogroup behaviour.
	const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
		if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
		event.preventDefault();
		const next = props.value === COMPOSER_MODE.COMMENT ? COMPOSER_MODE.REPLY_TO_NOVA : COMPOSER_MODE.COMMENT;
		select(next);
		(next === COMPOSER_MODE.COMMENT ? commentRef : replyRef).current?.focus();
	};

	const isReply = props.value === COMPOSER_MODE.REPLY_TO_NOVA;

	return (
		<Group role="radiogroup" aria-label={props.label} data-cy="task-modal-comment-mode-switch-control">
			<Chip
				type="button"
				ref={commentRef}
				role="radio"
				aria-checked={!isReply}
				tabIndex={isReply ? -1 : 0}
				isSelected={!isReply}
				onClick={() => select(COMPOSER_MODE.COMMENT)}
				onKeyDown={onKeyDown}
				data-cy="task-modal-comment-mode-comment"
			>
				{props.commentLabel}
			</Chip>
			<Chip
				type="button"
				ref={replyRef}
				role="radio"
				aria-checked={isReply}
				tabIndex={isReply ? 0 : -1}
				isSelected={isReply}
				onClick={() => select(COMPOSER_MODE.REPLY_TO_NOVA)}
				onKeyDown={onKeyDown}
				disabled={props.replyDisabled}
				title={props.replyDisabled ? props.replyDisabledReason : undefined}
				aria-describedby={showReason ? replyReasonId : undefined}
				data-cy="task-modal-comment-mode-reply"
			>
				{/* aria-hidden: the chip already carries its own accessible name, so the mark is
				    decoration that would otherwise be announced twice. */}
				<Icon icon="sparkle" size="s" aria-hidden="true" />
				{props.replyLabel}
			</Chip>
			{showReason && <HiddenReason id={replyReasonId}>{props.replyDisabledReason}</HiddenReason>}
		</Group>
	);
};
